import {
  Fragment,
  type Mark,
  type Node as ProseMirrorNode,
} from "prosemirror-model";

import { focusSchema } from "../schema";

const MENTION_REGEXP = /(^|[^\w])([@$])([A-Za-z0-9_][\w.-]*)/g;

const hasCodeMark = (marks: readonly Mark[]) =>
  marks.some((mark) => mark.type.name === "code");

const textWithMentionNodes = (
  text: string,
  marks: readonly Mark[],
): ProseMirrorNode[] => {
  if (hasCodeMark(marks)) {
    return [focusSchema.text(text, marks)];
  }

  const nodes: ProseMirrorNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(MENTION_REGEXP)) {
    const fullMatch = match[0] ?? "";
    const prefix = match[1] ?? "";
    const trigger = match[2] === "$" ? "$" : "@";
    const username = match[3] ?? "";
    const mentionStart = match.index! + prefix.length;

    if (mentionStart > lastIndex) {
      nodes.push(focusSchema.text(text.slice(lastIndex, mentionStart), marks));
    }

    nodes.push(
      focusSchema.nodes.mention.create({
        trigger,
        username,
      }),
    );

    lastIndex = match.index! + fullMatch.length;
  }

  if (lastIndex < text.length) {
    nodes.push(focusSchema.text(text.slice(lastIndex), marks));
  }

  return nodes.length ? nodes : [focusSchema.text(text, marks)];
};

export const hydratePlainTextMentions = (
  node: ProseMirrorNode,
): ProseMirrorNode => {
  if (node.isText) {
    return node;
  }

  if (node.isLeaf) {
    return node;
  }

  const children: ProseMirrorNode[] = [];

  node.forEach((child) => {
    if (child.isText && child.text) {
      children.push(...textWithMentionNodes(child.text, child.marks));
      return;
    }

    children.push(hydratePlainTextMentions(child));
  });

  return node.type.create(node.attrs, Fragment.fromArray(children), node.marks);
};
